import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Camera, Aperture, Sun, Cloud, FileImage, Award,
    Check, ChevronDown, ChevronUp,
    Star, ArrowRight,
} from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import BookingModal from "@/components/BookingModal";
import { fadeUp, Section, Kicker, MegaCTA } from "@/components/offer/parts";
import { useT } from "@/lib/i18n";
import axios from "axios";
import PackageCard from "@/components/pricing/PackageCard";
import TravelPolicy from "@/components/pricing/TravelPolicy";
import QuoteModal from "@/components/QuoteModal";
import { PRICE_FOOTNOTE, TRAVEL_ZONES, fetchTravelZones } from "@/lib/travel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "60183628663";

// ============ WhatsApp booking helpers ============
function whatsappBookingHref(tier, lang) {
    const text = lang === "cn"
        ? `Hi Kaily!我想预约 ${tier.name} 拍摄 (${tier.price})。请问最近什么时候有档期?`
        : `Hi Kaily! I'd like to book the ${tier.name} shoot (${tier.price}). When are you next available?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}
function whatsappGeneralHref(lang) {
    const text = lang === "cn"
        ? "Hi Kaily!我想预约拍摄,可以聊聊吗?"
        : "Hi Kaily! I'd like to book a shoot — can we chat?";
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

// ============ Bilingual content ============
const COPY = {
    en: {
        hero: {
            badge: "F&B Visual Studio · Malaysia",
            h1a: "Cinematic visuals",
            h1b: "for food & hospitality.",
            h1c: "",
            h1d: "",
            sub: "Brand films, social reels and food photography. Based in Malaysia. Available for international collaborations.",
            ctaBook: "See packages",
            ctaView: "WhatsApp Kaily",
            scroll: "Scroll · how it works",
        },
        stats: [
            { n: "1:1", l: "founder-shot · every project" },
            { n: "F&B", l: "specialists · menu + brand" },
            { n: "RM 388", l: "starting from" },
            { n: "Organic", l: "social usage · included" },
        ],
        problem: {
            kicker: "How we work",
            t: "Cinematic brand content for F&B and hospitality.",
            body: "Every project is scoped against the confirmed brief — service, deliverables, duration, locations and usage are all written into the quotation before booking. Fixed packages cover planned social reels; custom work is quoted individually.",
        },
        beforeAfter: {
            kicker: "Before / After",
            t: "From phone-grain to magazine-grade.",
            before: "Before · phone snap",
            after: "After · slowcafe shoot",
        },
        founder: {
            kicker: "KAILY GAN · FILMMAKER",
            t: "Meet Kaily.",
            body: "I'm Kaily, a Malaysia-based filmmaker creating cinematic stories for food and hospitality brands. From creative direction to filming and editing, I personally shape each project with a focus on food, feeling and atmosphere.",
            sign: "— Kaily, Malaysia",
        },
        inside: {
            kicker: "Every shoot includes",
            t: "Not a flat rate. A full creative engagement.",
            items: [
                { n: "01", t: "Half-day on-location", d: "4-hour block. KL & Selangor. Weekday or weekend, you pick." },
                { n: "02", t: "Professionally retouched images", d: "Hand-edited hi-res, ready to publish. Count depends on your package." },
                { n: "03", t: "Brand DNA brief", d: "30-min pre-call: palette, mood, menu hero, references." },
                { n: "04", t: "Social-ready formats", d: "Every image delivered in feed + story crops. Post straight away." },
                { n: "05", t: "7-day delivery", d: "Cloud-delivered gallery. Persistent links, never expire." },
                { n: "06", t: "Organic social usage included", d: "Paid advertising, broadcast and out-of-home licensing arranged separately in writing." },
            ],
        },
        showcase: { kicker: "Recent work", t: "Real cafes. Real plates. Real light." },
        tech: {
            kicker: "The approach",
            t: "Prime lenses · natural-first lighting · hand-graded colour.",
            body: "Prime lenses for sharp, cinematic depth. Natural light first; supplemental lighting when the kitchen kills sun. Every image hand-edited and colour-graded. No AI swap-outs. No stock comps.",
            stack: ["Prime lenses", "Natural light first", "Hand-graded colour", "Cloud gallery delivery"],
        },
        roi: {
            kicker: "The math",
            t: "vs. brand agency · RM 5,000+ · 6 weeks.",
            lines: [
                ["Brand agency · per shoot day", "RM 3,200"],
                ["Retoucher · per image", "RM 80 × 25"],
                ["Usage license · per year", "RM 1,200"],
                ["Project management · per project", "RM 600"],
            ],
            total: "= RM 7,000",
            kicker2: "slowcafe Menu Photoshoot",
            myPrice: "From RM 750",
            saveLine: "Scope confirmed in writing · ship in 7 days · organic social usage included",
        },
        perks: {
            kicker: "Why it lands well",
            t: "The details most studios skip.",
            items: [
                { i: Camera, t: "Shot by Kaily", d: "I show up. I shoot. I edit. No handoff." },
                { i: Aperture, t: "Prime glass only", d: "Sharp, cinematic depth. No kit-zoom flatness." },
                { i: Sun, t: "Natural light first", d: "Your space, your hour. Lit to feel real." },
                { i: FileImage, t: "Social-ready crops", d: "Feed + story formats for every image. No upsell." },
                { i: Award, t: "Organic social usage", d: "Included for all delivered content. Paid-media licensing arranged separately in writing." },
                { i: Cloud, t: "R2 cloud delivery", d: "Persistent links. No expiring WeTransfer." },
            ],
        },
        faq: {
            kicker: "Questions",
            t: "Frequently asked.",
            items: [
                { q: "Where do you shoot?", a: "Port Klang / Klang is complimentary. Shah Alam / Setia Alam RM30, Subang Jaya / Puchong / PJ RM50, Kuala Lumpur RM80. Outside Klang Valley and interstate by quotation — see Additional costs above." },
                { q: "Do I need to prep my space?", a: "We send a pre-shoot checklist 5 days before. Usually: clean plates, fresh ingredients, 30 min setup buffer." },
                { q: "Can I reschedule?", a: "Yes — free reschedule up to 48 hours before. Within 48h it's a 50% rebook fee." },
                { q: "How fast is delivery?", a: "Each project's delivery timeline, revision rounds and gallery storage window are confirmed in the written quotation before the deposit is paid." },
                { q: "Do you do video?", a: "Yes — Social Content Reels, Product Social Video, the Commercial Brand Reel and all Brand Campaign packages include video. Editing and music selection are included as listed on each card." },
                { q: "What if I need more than the package lists?", a: "Models, extra products or setups, complex styling, voice-over, extra versions, more locations or urgent delivery are quoted separately before production. The listed price never silently covers them." },
                { q: "What's the payment flow?", a: "50% deposit confirms the date. 50% on delivery. Bank transfer / Stripe / TNG." },
            ],
        },
        final: {
            kicker: "One quiet ask",
            t: "Let's give your work the light it deserves.",
            sub: "Tell me what you sell, when you're free, and how it should feel. I'll handle everything else.",
            ctaPrimary: "View Packages",
            ctaSecondary: "Discuss a Project",
        },
    },
    cn: {
        hero: {
            badge: "餐饮视觉工作室 · 马来西亚",
            h1a: "为餐饮与款待品牌",
            h1b: "打造电影感影像。",
            h1c: "",
            h1d: "",
            sub: "品牌影片、社交短片与餐饮摄影。驻地马来西亚,接受国际合作。",
            ctaBook: "查看套餐",
            ctaView: "WhatsApp 联系",
            scroll: "滚动 · 了解流程",
        },
        stats: [
            { n: "1:1", l: "创办人亲自拍摄每个项目" },
            { n: "餐饮", l: "专精 · 菜单与品牌" },
            { n: "RM 388", l: "起步价" },
            { n: "自然", l: "社媒发布使用权 · 已含" },
        ],
        problem: {
            kicker: "残酷真相",
            t: "手机拍不出 RM 38 的拿铁。糟糕的光线会先于价格毁掉你的菜单。",
            body: "大多数咖啡馆要么花 RM 5,000+ 找品牌代理(每次复用还要再加钱、等 6 周交付),要么自己用 iPhone 等黄金时刻碰运气。我们做了第三个选择。",
        },
        beforeAfter: {
            kicker: "前 / 后对比",
            t: "从手机噪点到杂志级。",
            before: "之前 · 手机随拍",
            after: "之后 · slowcafe 拍摄",
        },
        founder: {
            kicker: "KAILY GAN · 导演",
            t: "认识 Kaily。",
            body: "你好,我是 Kaily,一位驻地马来西亚的影片导演,为餐饮与款待品牌打造电影感故事。从创意方向、拍摄到剪辑,我亲自参与每个项目,专注于食物、感受与氛围。",
            sign: "— Kaily,马来西亚",
        },
        inside: {
            kicker: "每场拍摄包含",
            t: "不是固定套餐,是一次完整创作。",
            items: [
                { n: "01", t: "半日上门拍摄", d: "4 小时档,KL 与雪兰莪,工作日或周末由你定。" },
                { n: "02", t: "专业精修图", d: "亲手修图、高分辨率、即发即用,数量按套餐而定。" },
                { n: "03", t: "品牌 DNA 简报", d: "30 分钟预沟通:色调、氛围、菜单主角、参考方向。" },
                { n: "04", t: "社媒即用尺寸", d: "每张图附 Feed + Story 裁切版本,拿到即发。" },
                { n: "05", t: "7 天交付", d: "云端交付,链接永不过期。" },
                { n: "06", t: "自然社媒发布使用权", d: "付费广告、广播及户外投放授权另行书面约定。" },
            ],
        },
        showcase: { kicker: "近期作品", t: "真实咖啡馆。真实菜品。真实光线。" },
        tech: {
            kicker: "拍摄方式",
            t: "定焦镜头 · 自然光优先 · 手工调色。",
            body: "全程定焦镜头,锐利、有电影感。优先自然光,厨房光不足时补光。每张图手工剪辑与调色。无 AI 替换,无图库拼贴。",
            stack: ["定焦镜头", "自然光优先", "手工调色", "云端相册交付"],
        },
        roi: {
            kicker: "价值对比",
            t: "vs 品牌代理 · RM 5,000+ · 6 周。",
            lines: [
                ["品牌代理 · 单日拍摄", "RM 3,200"],
                ["修图 · 单张", "RM 80 × 25"],
                ["使用授权 · 每年", "RM 1,200"],
                ["项目管理 · 每项目", "RM 600"],
            ],
            total: "= RM 7,000",
            kicker2: "slowcafe Menu Photoshoot",
            myPrice: "RM 750 起",
            saveLine: "书面确认范围 · 7 天交付 · 含自然社媒发布使用权",
        },
        perks: {
            kicker: "为什么值",
            t: "其他工作室会跳过的细节。",
            items: [
                { i: Camera, t: "Kaily 亲自拍摄", d: "我到场、我拍、我修。无中转。" },
                { i: Aperture, t: "全程定焦镜头", d: "锐利、电影感、有空间感。" },
                { i: Sun, t: "自然光优先", d: "你的空间、你的时段,光线真实。" },
                { i: FileImage, t: "社媒即用尺寸", d: "每张图附 Feed + Story 版本,无加价。" },
                { i: Award, t: "自然社媒发布使用权", d: "交付内容包含,付费广告授权另行书面约定。" },
                { i: Cloud, t: "R2 云端交付", d: "链接永不过期,告别 WeTransfer 倒计时。" },
            ],
        },
        faq: {
            kicker: "常见问题",
            t: "你可能想问。",
            items: [
                { q: "在哪里拍摄?", a: "巴生港 / 巴生免费。莎阿南 / 实达阿南 RM30,梳邦再也 / 蒲种 / PJ RM50,吉隆坡 RM80。巴生谷以外及跨州另行报价 — 详见上方「附加费用」。" },
                { q: "我需要准备场地吗?", a: "拍摄前 5 天会发清单。通常:餐具洁净、食材新鲜、留 30 分钟布置缓冲。" },
                { q: "可以改期吗?", a: "可以。提前 48 小时免费改期。48 小时内收 50% 改期费。" },
                { q: "交付要多快?", a: "每个项目的交付时间、修改轮次与相册储存期限以预订前书面报价为准。" },
                { q: "拍视频吗?", a: "拍。社媒内容短视频、产品社媒视频、商业品牌短视频及所有品牌宣传套餐都含视频。剪辑与配乐挑选按各卡片所列包含。" },
                { q: "如果我需要的比套餐列的更多?", a: "模特、额外产品或场景、复杂造型、配音、额外版本、更多地点或加急交付,都会在制作前另行报价。所列价格不会默默涵盖这些项目。" },
                { q: "怎么付款?", a: "50% 定金锁档期,50% 交付时付。支持银行转账 / Stripe / TNG。" },
            ],
        },
        final: {
            kicker: "一个安静的请求",
            t: "让我们给你的作品应得的光线。",
            sub: "告诉我你卖什么、什么时候有空、你希望它呈现什么感觉。剩下的交给我。",
            ctaPrimary: "查看套餐",
            ctaSecondary: "开启项目",
        },
    },
};

// ============ Mobile sticky CTA (View Services + WhatsApp) ============
// Simplified Feb 2026 release-check — replaced the old RM 388 price banner
// with two neutral actions. Uses the editorial palette (cream · olive ·
// espresso) and matching serif typography. Appears after 600px scroll and
// slides up from the bottom; body already has `pb-[76px]` on mobile so it
// never covers the final CTA.
function MobileStickyCTA({ lang }) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        const onScroll = () => setShow(window.scrollY > 600);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    const waHref = whatsappGeneralHref(lang);
    return (
        <div
            data-testid="services-mobile-sticky-cta"
            className={[
                "md:hidden fixed bottom-0 inset-x-0 z-40",
                "bg-cafe-cream border-t border-cafe-espresso/10",
                "shadow-[0_-10px_30px_-10px_rgba(26,15,10,0.12)]",
                "transition-transform duration-300",
                show ? "translate-y-0" : "translate-y-full",
            ].join(" ")}
        >
            <div className="grid grid-cols-2 gap-2 p-2.5">
                <a
                    href="#services-pricing"
                    data-testid="services-sticky-cta-view"
                    className="flex items-center justify-center gap-2 py-3 px-3 rounded-full bg-cafe-espresso text-cafe-cream text-[12.5px] font-medium tracking-wide active:scale-[0.97] transition-transform"
                >
                    {lang === "cn" ? "查看套餐" : "View Services"}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
                </a>
                <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="services-sticky-cta-chat"
                    className="flex items-center justify-center gap-2 py-3 px-3 rounded-full border border-cafe-espresso text-cafe-espresso text-[12.5px] font-medium tracking-wide active:scale-[0.97] transition-transform"
                >
                    {lang === "cn" ? "WhatsApp Kaily" : "WhatsApp Kaily"}
                </a>
            </div>
        </div>
    );
}


// ---- Hero ----
function HeroPhotoCollage({ images }) {
    const cells = images.slice(0, 6);
    while (cells.length < 6) cells.push(null);
    return (
        <div className="relative">
            <div className="absolute -inset-10 bg-[radial-gradient(ellipse_at_center,_rgba(212,165,116,0.18),_transparent_60%)] pointer-events-none" />
            <div className="relative grid grid-cols-3 gap-2 md:gap-3 transform md:rotate-[-2deg]">
                {cells.map((src, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.1 + i * 0.06 }}
                        className={`overflow-hidden bg-cafe-cream/5 ${i === 1 ? "row-span-2 aspect-[3/5]" : "aspect-square"} shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]`}
                    >
                        {src && <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />}
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

function Hero({ c, lang, images, siteMeta }) {
    // Services page hero — Feb 2026 layout refresh.
    // Desktop: left column ~52% (text + CTAs), right column ~42% (portrait
    // image at 4:5). Gap reduced from 16 → 8 so the middle doesn't yawn.
    // Mobile: title → image → sub → buttons (image is big, not cropped
    // out). Both breakpoints use independent image + crop pulled from
    // site_meta so Kaily can pin the person-with-umbrella focal point
    // separately on each device.
    //
    // Title uses the same editorial serif as the homepage, with an explicit
    // two-line break and no italics (minor emphasis handled with the olive
    // colour accent on the second line).
    const imgD = siteMeta?.services_hero_image || "";
    const imgM = siteMeta?.services_hero_image_mobile || "";
    const cropD = siteMeta?.services_hero_crop || { x: 50, y: 50 };
    const cropM = siteMeta?.services_hero_mobile_crop || { x: 50, y: 50 };
    const srcD = imgD || imgM;
    const srcM = imgM || imgD;
    const effCropD = imgD ? cropD : cropM;
    const effCropM = imgM ? cropM : cropD;
    const hasAnyImage = !!(srcD || srcM);

    return (
        <section
            className="relative overflow-hidden bg-cafe-cream text-cafe-espresso pt-20 md:pt-24 pb-8 md:pb-12"
            data-testid="services-hero"
        >
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-[52%_42%] gap-x-[6%] gap-y-10 lg:gap-y-0 items-center">
                {/* --- Text column --- */}
                <motion.div {...fadeUp} className="order-1 lg:order-1">
                    <div className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-4" data-testid="services-hero-badge">
                        {c.badge}
                    </div>
                    <h1
                        className="font-serif tracking-[-0.02em] text-cafe-espresso text-[clamp(2.25rem,5.2vw,4rem)] leading-[1.02] mb-5"
                        style={{ fontWeight: 500 }}
                        data-testid="services-hero-headline"
                    >
                        <span className="block">{c.h1a}</span>
                        <span className="block">{c.h1b}</span>
                    </h1>
                    <p className="text-cafe-espresso/70 text-base md:text-lg leading-relaxed max-w-xl mb-8 hidden lg:block" data-testid="services-hero-sub-desktop">
                        {c.sub}
                    </p>
                    <div className="hidden lg:flex flex-wrap gap-3" data-testid="services-hero-ctas-desktop">
                        <a href="#services-pricing" data-testid="services-hero-cta-book"
                            className="inline-flex items-center h-12 px-6 rounded-full bg-cafe-espresso text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-olive transition-colors">
                            {c.ctaBook}
                        </a>
                        <a href={whatsappGeneralHref(lang)} target="_blank" rel="noreferrer" data-testid="services-hero-cta-chat"
                            className="inline-flex items-center h-12 px-6 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors">
                            {c.ctaView}
                        </a>
                    </div>
                </motion.div>

                {/* --- Image column --- */}
                {/* The image sits between the headline and the sub-text on
                    phones (order-2) so Kaily's actual work shows up right
                    after the title. On desktop it moves to the right column. */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="order-2 lg:order-2 lg:justify-self-end w-full"
                    data-testid="services-hero-image-col"
                >
                    <div className="aspect-[4/5] bg-cafe-olive overflow-hidden relative w-full max-w-[520px] lg:max-w-[460px] mx-auto">
                        {hasAnyImage ? (
                            <>
                                {/* Mobile image + mobile crop (< md). */}
                                <img
                                    src={srcM}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover md:hidden"
                                    style={{ objectPosition: `${effCropM.x}% ${effCropM.y}%` }}
                                    data-testid="services-hero-image-mobile"
                                />
                                {/* Desktop image + desktop crop (md+). */}
                                <img
                                    src={srcD}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover hidden md:block"
                                    style={{ objectPosition: `${effCropD.x}% ${effCropD.y}%` }}
                                    data-testid="services-hero-image"
                                />
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-cafe-cream/60 font-display italic text-sm">
                                Original project still
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* --- Sub + CTAs for mobile (below image) --- */}
                <div className="order-3 lg:hidden" data-testid="services-hero-sub-mobile">
                    <p className="text-cafe-espresso/70 text-base leading-relaxed mb-6 max-w-xl">{c.sub}</p>
                    <div className="flex flex-wrap gap-3">
                        <a href="#services-pricing" data-testid="services-hero-cta-book-mobile"
                            className="inline-flex items-center h-12 px-6 rounded-full bg-cafe-espresso text-cafe-cream text-[13.5px] font-medium">
                            {c.ctaBook}
                        </a>
                        <a href={whatsappGeneralHref(lang)} target="_blank" rel="noreferrer" data-testid="services-hero-cta-chat-mobile"
                            className="inline-flex items-center h-12 px-6 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium">
                            {c.ctaView}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

function StatsBar({ stats }) {
    return (
        <div className="bg-[#0F0805] border-b border-cafe-cream/10 py-12 md:py-16 px-6 md:px-12">
            <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
                {stats.map((s, i) => (
                    <motion.div
                        key={i}
                        {...fadeUp}
                        transition={{ duration: 0.6, delay: i * 0.08 }}
                        className="text-center md:text-left"
                        data-testid={`services-stat-${i}`}
                    >
                        <div className="font-display text-[#D4A574] text-4xl md:text-6xl tracking-[-0.03em] mb-2">{s.n}</div>
                        <div className="text-cafe-cream/55 text-[12px] md:text-sm tracking-wide leading-snug">{s.l}</div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

function Problem({ c }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em] mb-8">{c.t}</h2>
                <p className="font-light text-cafe-mocha text-lg md:text-xl leading-relaxed max-w-3xl">{c.body}</p>
            </motion.div>
        </Section>
    );
}

function BeforeAfter({ c, images }) {
    // Real gallery images only — the left column shows the same set desaturated
    // as a lighting/grade comparison. No stock placeholders.
    const afterImgs = images.slice(0, 6);
    const beforeImgs = afterImgs;
    if (afterImgs.length < 6) return null;
    return (
        <Section dark>
            <motion.div {...fadeUp} className="mb-14 max-w-3xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                {[{ title: c.before, list: beforeImgs, mono: true }, { title: c.after, list: afterImgs, mono: false }].map((col, idx) => (
                    <motion.div key={idx} {...fadeUp} transition={{ duration: 0.7, delay: idx * 0.15 }}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-[11px] uppercase tracking-[0.28em] text-cafe-cream/50">{col.title}</div>
                            {!col.mono && <Star className="h-4 w-4 text-[#D4A574]" strokeWidth={1.6} fill="#D4A574" />}
                        </div>
                        <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                            {col.list.map((src, i) => (
                                <div key={i} className="aspect-square bg-cafe-cream/5 overflow-hidden">
                                    <img src={src} alt="" loading="lazy" className={`w-full h-full object-cover transition-all ${col.mono ? "grayscale opacity-60 blur-[0.5px]" : ""}`} />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

function FounderStory({ c, siteMeta }) {
    // Portrait + bio pulled from Studio's Website Content tab. Falls back to
    // the K. placeholder (olive tile) if Kaily hasn't uploaded a photo yet.
    const portrait = siteMeta?.meet_kaily_image || "";
    const portraitCrop = siteMeta?.meet_kaily_crop || { x: 50, y: 50 };
    const role = (siteMeta?.meet_kaily_role || c.kicker || "KAILY GAN · FILMMAKER").trim();
    const bio = (siteMeta?.meet_kaily_bio || c.body || "").trim();
    return (
        <Section>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
                <motion.div {...fadeUp} className="md:col-span-5">
                    <div className="aspect-[4/5] bg-[#F0E8DD] border border-cafe-espresso/10 overflow-hidden" data-testid="meet-kaily-portrait">
                        {portrait ? (
                            <img src={portrait} alt="Kaily Gan"
                                className="w-full h-full object-cover"
                                style={{ objectPosition: `${portraitCrop.x}% ${portraitCrop.y}%` }} />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <div className="text-center px-8">
                                    <div className="font-display text-7xl text-cafe-olive/80">K.</div>
                                    <div className="mt-3 text-xs uppercase tracking-[0.3em] text-cafe-mocha/60">kaily_gan</div>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
                <motion.div {...fadeUp} className="md:col-span-7 md:pt-12">
                    <Kicker>{role}</Kicker>
                    <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.08] tracking-[-0.03em] mb-8" style={{ fontWeight: 500 }}>{c.t}</h2>
                    <p className="font-light text-cafe-mocha text-lg leading-relaxed mb-6" data-testid="meet-kaily-bio">{bio}</p>
                    <p className="font-display italic text-cafe-olive text-lg">{c.sign}</p>
                </motion.div>
            </div>
        </Section>
    );
}

function WhatsInside({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="mb-16 md:mb-20 max-w-3xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cafe-cream/10">
                {c.items.map((it, i) => (
                    <motion.div
                        key={it.n}
                        {...fadeUp}
                        transition={{ duration: 0.6, delay: i * 0.05 }}
                        className="bg-[#1A0F0A] p-10 md:p-12 hover:bg-[#221610] transition-colors"
                        data-testid={`services-inside-${it.n}`}
                    >
                        <div className="font-display text-[#D4A574] text-2xl mb-6 tracking-[-0.02em]">{it.n}</div>
                        <div className="font-display text-cafe-cream text-2xl md:text-3xl mb-3 tracking-[-0.02em]">{it.t}</div>
                        <p className="font-light text-cafe-cream/60 leading-relaxed">{it.d}</p>
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

function Showcase({ c, images }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-14 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {images.slice(0, 12).map((src, i) => (
                    <motion.div
                        key={i}
                        {...fadeUp}
                        transition={{ duration: 0.55, delay: i * 0.03 }}
                        className={`overflow-hidden bg-[#F0E8DD] ${i % 5 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"}`}
                    >
                        <img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

function TechStack({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.03em] mb-8">{c.t}</h2>
                <p className="font-light text-cafe-cream/70 text-lg leading-relaxed mb-10 max-w-2xl">{c.body}</p>
                <div className="flex flex-wrap gap-3">
                    {c.stack.map((s) => (
                        <span key={s} className="border border-cafe-cream/20 text-cafe-cream/85 text-sm px-5 py-2.5 rounded-full font-light hover:border-[#D4A574] hover:text-[#D4A574] transition-colors">{s}</span>
                    ))}
                </div>
            </motion.div>
        </Section>
    );
}

// ---- Pricing — fetched LIVE from /api/packages so Studio edits go live instantly ----
const PRICING_COPY = {
    en: {
        social: { kicker: "Social Content Packages", t: "Clear scope. Clear price. Book with confidence.", sub: "Every package lists exactly what is included, what is not, and when a custom quotation is needed — so you know what you receive before you book." },
        campaign: { kicker: "Brand Campaign Packages", t: "Photo + video campaigns, scoped in writing.", sub: "Prices are based only on the inclusions listed. Deliverables, production hours, locations, revision limits and any additional charges are confirmed in writing before the deposit is paid." },
        conditionsTitle: "Brand Campaign package conditions",
        conditions: [
            "The listed Brand Campaign prices are based only on the inclusions stated in each card.",
            "All final deliverables, production hours, locations, revision limits and additional charges must be confirmed in writing before the deposit is paid.",
            "Any request outside the listed scope may require a revised quotation, additional production hours, additional crew or equipment, additional editing fees, additional travel charges, or location, studio or permit fees.",
            "Professional models, unlimited revisions, unlimited locations, unlimited products or unlimited deliverables are not included in any Brand Campaign package.",
        ],
        loading: "Loading packages…", error: "Packages could not be loaded right now — WhatsApp Kaily for the current price list.",
        helper: "50% deposit locks your date · balance on delivery · scope confirmed in writing before booking",
    },
    cn: {
        social: { kicker: "社媒内容套餐", t: "范围清楚,价格清楚,放心预订。", sub: "每个套餐都列明包含什么、不包含什么,以及何时需要定制报价 — 让你在预订前就知道会收到什么。" },
        campaign: { kicker: "品牌宣传套餐", t: "照片 + 视频宣传,书面确认范围。", sub: "价格仅基于所列包含项目。交付物、拍摄时数、地点、修改次数及任何额外费用,均在支付定金前以书面确认。" },
        conditionsTitle: "品牌宣传套餐条件",
        conditions: [
            "所列品牌宣传价格仅基于各卡片中列明的包含项目。",
            "所有最终交付物、拍摄时数、地点、修改次数及额外费用,须在支付定金前以书面确认。",
            "任何超出所列范围的需求,可能需要修订报价、额外拍摄时数、额外人员或器材、额外剪辑费用、额外旅程费用,或场地、影棚或许可证费用。",
            "任何品牌宣传套餐均不包含专业模特、无限修改、无限地点、无限产品或无限交付物。",
        ],
        loading: "正在加载套餐…", error: "暂时无法加载套餐 — 请 WhatsApp Kaily 获取最新价格表。",
        helper: "50% 定金锁档期 · 交付后付尾款 · 预订前书面确认范围",
    },
};

function mapPackage(p, lang) {
    const cn = lang === "cn";
    const pick = (k) => (cn ? p[`${k}_cn`] : p[`${k}_en`]) || (cn ? p[`${k}_en`] : "") || "";
    const list = (k) => (cn ? p[`${k}_cn`] : p[`${k}_en`]) || (cn ? p[`${k}_en`] : []) || [];
    const priceNum = parseFloat(String(p.price || "0").replace(/,/g, "")) || 0;
    return {
        id: p.id, package_id: p.id,
        name: pick("name") || p.id, name_en: p.name_en, name_cn: p.name_cn,
        price: p.price ? `RM ${p.price}` : "", price_rm: priceNum, price_prefix: p.price_prefix || "",
        section: p.section || "social", booking_mode: p.booking_mode || "deposit", travel_included_kv: !!p.travel_included_kv,
        note: pick("description"), bullets: list("features"), excludes: list("excludes"),
        duration: pick("duration"), locations: pick("locations"), revisions: pick("revisions"), travel: pick("travel"),
        custom_quote: pick("custom_quote"), scope_note: pick("scope_note"), callout: pick("callout"),
        quote_depends: list("quote_depends"), conditions: list("conditions"), pop: !!p.popular,
    };
}

function quotationHref(tier, lang) {
    const price = `${tier.price_prefix ? (lang === "cn" ? "" : "From ") : ""}${tier.price}${tier.price_prefix && lang === "cn" ? " 起" : ""}`;
    const text = lang === "cn"
        ? `Hi Kaily!我想为 ${tier.name}(${price})索取详细报价。可以先了解一下我的需求吗?`
        : `Hi Kaily! I'd like a detailed quotation for ${tier.name} (${price}). Could we go through my requirements?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
}

function PricingGroup({ id, copy, tiers, lang, onBook, onQuote, testid, children }) {
    // One-at-a-time accordion. Lifted here (not into PackageCard) so clicking
    // tier B automatically collapses tier A — the user asked for this to keep
    // the mobile scrolling short.
    const [expandedId, setExpandedId] = useState(null);
    return (
        <Section id={id}>
            <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
                <Kicker>{copy.kicker}</Kicker>
                <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em] mb-6">{copy.t}</h2>
                <p className="font-light text-cafe-mocha text-lg leading-relaxed">{copy.sub}</p>
            </motion.div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 items-start" data-testid={testid}>
                {tiers.map((t, idx) => (
                    <PackageCard
                        key={t.id}
                        pkg={t}
                        idx={idx}
                        lang={lang}
                        expanded={expandedId === t.id}
                        onToggle={() => setExpandedId((cur) => (cur === t.id ? null : t.id))}
                        onBook={onBook}
                        onQuote={onQuote}
                        quoteHref={quotationHref(t, lang)}
                        chatHref={whatsappBookingHref(t, lang)}
                    />
                ))}
            </div>
            {children}
        </Section>
    );
}

export function usePackages(lang) {
    const [state, setState] = useState({ tiers: [], addons: [], zones: TRAVEL_ZONES, status: "loading" });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const zones = await fetchTravelZones();
            try {
                const r = await axios.get(`${API}/packages`, { timeout: 8000, params: { _t: Date.now() }, headers: { "Cache-Control": "no-cache" } });
                const all = Array.isArray(r.data) ? r.data : [];
                const bySort = (a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99);
                const tiers = all.filter((p) => (p.kind || "shoot") === "shoot" && p.active !== false).sort(bySort).map((p) => mapPackage(p, lang));
                const addons = all.filter((p) => p.kind === "addon" && p.active !== false).sort(bySort).map((p) => ({
                    id: p.id, name: (lang === "cn" ? p.name_cn : p.name_en) || p.id,
                    price: p.price ? `RM ${p.price}` : "", note: (lang === "cn" ? p.description_cn : p.description_en) || "",
                }));
                if (!cancelled) setState({ tiers, addons, zones, status: "ok" });
            } catch {
                if (!cancelled) setState({ tiers: [], addons: [], zones, status: "error" });
            }
        })();
        return () => { cancelled = true; };
    }, [lang]);
    return state;
}

function Pricing({ lang, tiers, addons, zones, status }) {
    const [modalPkg, setModalPkg] = useState(null);
    const [quotePkg, setQuotePkg] = useState(null);
    const c = PRICING_COPY[lang] || PRICING_COPY.en;
    const social = tiers.filter((t) => t.section !== "campaign");
    const campaign = tiers.filter((t) => t.section === "campaign");

    return (
        <div id="services-pricing">
            {status !== "ok" && (
                <Section>
                    <p className="text-center text-cafe-mocha" data-testid="services-pricing-status">{status === "loading" ? c.loading : c.error}</p>
                </Section>
            )}
            {social.length > 0 && (
                <PricingGroup id="social-content-packages" copy={c.social} tiers={social} lang={lang} onBook={setModalPkg} onQuote={setQuotePkg} testid="services-social-packages" />
            )}
            {campaign.length > 0 && (
                <div className="bg-[#F0E8DD]/60">
                    <PricingGroup id="brand-campaign-packages" copy={c.campaign} tiers={campaign} lang={lang} onBook={setModalPkg} onQuote={setQuotePkg} testid="services-campaign-packages" />
                </div>
            )}

            {status === "ok" && (
                <div className="max-w-7xl mx-auto px-6 md:px-12 pb-6">
                    <motion.p {...fadeUp} className="text-center text-cafe-mocha/60 text-xs md:text-sm tracking-[0.22em] uppercase font-medium">
                        <span className="inline-block h-px w-12 align-middle mr-3 bg-cafe-olive/40" />{c.helper}<span className="inline-block h-px w-12 align-middle ml-3 bg-cafe-olive/40" />
                    </motion.p>
                </div>
            )}

            {addons.length > 0 && (
                <Section>
                    <motion.div {...fadeUp} data-testid="services-addons">
                        <Kicker>{lang === "cn" ? "可选加购" : "Optional add-ons"}</Kicker>
                        <h3 className="font-display text-2xl md:text-4xl tracking-[-0.025em] text-cafe-espresso leading-tight mb-2 max-w-3xl">{lang === "cn" ? "让套餐更贴合你的需求。" : "Tailor any package to fit your needs."}</h3>
                        <p className="text-cafe-mocha max-w-2xl mb-10">{lang === "cn" ? "在 WhatsApp 与 Kaily 沟通时告诉她你需要哪些加购,会附加在定金发票上。" : "Mention any add-ons when you WhatsApp Kaily — they'll be itemised on the deposit invoice."}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                            {addons.map((a) => (
                                <div key={a.id} data-testid={`services-addon-${a.id}`} className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5 hover:border-cafe-olive/40 transition-all">
                                    <div className="flex items-baseline justify-between gap-3 mb-2"><h4 className="font-display text-lg text-cafe-espresso leading-tight">{a.name}</h4><span className="font-display text-sm text-[#B88A55] tabular-nums whitespace-nowrap">{a.price}</span></div>
                                    {a.note && <p className="text-sm text-cafe-mocha leading-relaxed">{a.note}</p>}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </Section>
            )}

            <BookingModal
                open={!!modalPkg}
                onClose={() => setModalPkg(null)}
                pkg={modalPkg ? { id: modalPkg.package_id, name_en: modalPkg.name_en || modalPkg.name, name_cn: modalPkg.name_cn || modalPkg.name, price_rm: modalPkg.price_rm, travel_included_kv: modalPkg.travel_included_kv } : null}
                lang={lang}
                zones={zones}
            />
            <QuoteModal
                key={quotePkg?.id || "none"}
                open={!!quotePkg}
                onClose={() => setQuotePkg(null)}
                pkg={quotePkg ? { id: quotePkg.package_id, name_en: quotePkg.name_en || quotePkg.name, name_cn: quotePkg.name_cn || quotePkg.name } : null}
                lang={lang}
                zones={zones}
                source={quotePkg?.booking_mode === "quote" ? "quote_cta" : "out_of_scope"}
            />
        </div>
    );
}

const TERMS = {
    en: {
        kicker: "Booking terms", t: "How a booking is confirmed.",
        items: [
            ["Scope in writing", "Deliverables, production hours, locations, revision limits and any additional charges are confirmed in writing before the deposit is paid."],
            ["50% deposit", "A 50% deposit confirms the booking and locks the date. The balance is payable on delivery."],
            ["Travel & expenses", "Travel is charged per the Travel & Location Policy. Toll, parking, accommodation, location fees and transport between locations are charged at actual cost."],
            ["Reschedule", "Free reschedule up to 48 hours before the shoot. Within 48 hours a 50% rebooking fee applies."],
            ["Out-of-scope requests", "Anything outside the listed package scope is quoted separately before it is produced."],
        ],
    },
    cn: {
        kicker: "预订条款", t: "预订如何确认。",
        items: [
            ["书面确认范围", "交付物、拍摄时数、地点、修改次数及任何额外费用,在支付定金前以书面确认。"],
            ["50% 定金", "50% 定金确认预订并锁定档期。尾款在交付时支付。"],
            ["旅程与开支", "旅程按「旅程与地点政策」收费。过路费、停车费、住宿、场地费用及地点之间的交通按实际费用收取。"],
            ["改期", "拍摄前 48 小时以上可免费改期。48 小时内收取 50% 改期费。"],
            ["超出范围的需求", "任何超出所列套餐范围的项目,在制作前另行报价。"],
        ],
    },
};

function BookingTerms({ lang }) {
    const c = TERMS[lang] || TERMS.en;
    return (
        <Section dark id="booking-terms">
            <motion.div {...fadeUp} className="mb-12 max-w-3xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cafe-cream/10" data-testid="booking-terms">
                {c.items.map(([t, d], i) => (
                    <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.05 }} className="bg-[#1A0F0A] p-8 md:p-10">
                        <div className="font-display text-[#D4A574] text-xl mb-3 tracking-[-0.02em]">{String(i + 1).padStart(2, "0")}</div>
                        <div className="font-display text-cafe-cream text-xl md:text-2xl mb-2 tracking-[-0.02em]">{t}</div>
                        <p className="font-light text-cafe-cream/60 text-sm leading-relaxed">{d}</p>
                    </motion.div>
                ))}
            </div>
        </Section>
    );
}

function ROI({ c }) {
    return (
        <Section dark>
            <motion.div {...fadeUp} className="max-w-4xl">
                <Kicker dark>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.03em] mb-12">{c.t}</h2>
                <div className="space-y-px bg-cafe-cream/10 border border-cafe-cream/10 mb-10">
                    {c.lines.map((line, i) => (
                        <motion.div
                            key={i}
                            {...fadeUp}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            className="flex items-baseline justify-between bg-[#1A0F0A] p-5 md:p-6"
                        >
                            <span className="font-light text-cafe-cream/75 text-sm md:text-base">{line[0]}</span>
                            <span className="font-display text-xl md:text-2xl text-cafe-cream/90 line-through decoration-cafe-cream/30 tracking-[-0.02em]">{line[1]}</span>
                        </motion.div>
                    ))}
                </div>
                <div className="flex items-baseline justify-end gap-4 pt-4 border-t border-cafe-cream/20 mb-12">
                    <span className="text-cafe-cream/50 text-sm uppercase tracking-[0.22em]">Agency total</span>
                    <span className="font-display text-3xl md:text-5xl text-cafe-cream/80 line-through decoration-cafe-cream/40 tracking-[-0.03em]">{c.total}</span>
                </div>
                <div className="bg-[#221610] border border-[#D4A574]/40 rounded-2xl p-8 md:p-12">
                    <div className="text-[#D4A574] text-[11px] uppercase tracking-[0.28em] mb-3">{c.kicker2}</div>
                    <div className="flex items-baseline gap-5 mb-5">
                        <span className="font-display text-5xl md:text-7xl text-[#D4A574] tracking-[-0.03em]">{c.myPrice}</span>
                    </div>
                    <p className="font-light text-cafe-cream/75 text-base md:text-lg">{c.saveLine}</p>
                </div>
            </motion.div>
        </Section>
    );
}

function Perks({ c }) {
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-16 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                {c.items.map((it) => {
                    const Icon = it.i;
                    return (
                        <motion.div key={it.t} {...fadeUp}>
                            <div className="h-12 w-12 mb-5 rounded-full bg-cafe-olive/10 flex items-center justify-center">
                                <Icon className="h-5 w-5 text-cafe-olive" strokeWidth={1.5} />
                            </div>
                            <div className="font-display text-xl text-cafe-espresso mb-2 tracking-[-0.02em]">{it.t}</div>
                            <p className="font-light text-cafe-mocha leading-relaxed">{it.d}</p>
                        </motion.div>
                    );
                })}
            </div>
        </Section>
    );
}


function FAQ({ c }) {
    const [open, setOpen] = useState(0);
    return (
        <Section>
            <motion.div {...fadeUp} className="mb-12 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]">{c.t}</h2>
            </motion.div>
            <div className="max-w-3xl">
                {c.items.map((it, i) => {
                    const isOpen = open === i;
                    return (
                        <div key={i} className="border-b border-cafe-espresso/15">
                            <button
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                data-testid={`services-faq-${i}`}
                                className="w-full flex items-center justify-between text-left py-6 group"
                            >
                                <span className="font-display text-lg md:text-xl text-cafe-espresso pr-6 tracking-[-0.01em]">{it.q}</span>
                                {isOpen
                                    ? <ChevronUp className="h-5 w-5 text-cafe-olive flex-shrink-0" strokeWidth={1.5} />
                                    : <ChevronDown className="h-5 w-5 text-cafe-mocha flex-shrink-0 group-hover:text-cafe-olive transition-colors" strokeWidth={1.5} />}
                            </button>
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                        className="overflow-hidden"
                                    >
                                        <p className="font-light text-cafe-mocha leading-relaxed pb-6 pr-10">{it.a}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </Section>
    );
}

function Guarantee({ c }) {
    return (
        <div className="bg-cafe-olive text-cafe-cream py-5 px-6 md:px-12" data-testid="services-guarantee">
            <motion.div {...fadeUp} className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base font-light tracking-wide text-center">
                <Check className="h-4 w-4" strokeWidth={2} />
                {c}
            </motion.div>
        </div>
    );
}

function FinalCTA({ c, lang }) {
    // Simplified Feb 2026 release-check — no more RM 388 price pill, no
    // "Book a Shoot" CTA that implies instant checkout. Two plain actions:
    // scroll back to packages, or open WhatsApp. Lighter cream palette so
    // the dark gradient slab doesn't dominate the end of the page.
    return (
        <section className="bg-cafe-espresso text-cafe-cream py-20 md:py-28 px-6 md:px-12 text-center" data-testid="services-final-cta">
            <motion.div {...fadeUp} className="relative max-w-2xl mx-auto">
                <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-[#D4A574] mb-4">{c.kicker}</p>
                <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-cafe-cream leading-[1.08] tracking-[-0.03em] mb-6" style={{ fontWeight: 500 }}>{c.t}</h2>
                <p className="font-light text-cafe-cream/70 text-base md:text-lg mb-10 max-w-xl mx-auto">{c.sub}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <a href="#services-pricing" data-testid="services-final-cta-view"
                        className="inline-flex items-center h-12 px-7 rounded-full bg-cafe-cream text-cafe-espresso text-[13.5px] font-medium hover:-translate-y-0.5 transition-transform">
                        {c.ctaPrimary}
                        <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} />
                    </a>
                    <a href={whatsappGeneralHref(lang)} target="_blank" rel="noreferrer" data-testid="services-final-cta-chat"
                        className="inline-flex items-center h-12 px-7 rounded-full border border-cafe-cream/60 text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-cream hover:text-cafe-espresso transition-colors">
                        {c.ctaSecondary}
                    </a>
                </div>
            </motion.div>
        </section>
    );
}

// ============ Page ============
export default function Services() {
    const { lang } = useT();
    const c = COPY[lang] || COPY.en;
    const [images, setImages] = useState([]);
    const [siteMeta, setSiteMeta] = useState(null);
    const packages = usePackages(lang);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/gallery`, { params: { limit: 24 } });
                const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
                const urls = items.map((it) => it.url || it.image_url || it.src).filter(Boolean);
                if (!cancelled) setImages(urls.slice(0, 16));  // real work only — no stock padding
            } catch {
                if (!cancelled) setImages([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Fetch Studio-managed site-meta (hero image, Kaily portrait, bio).
    useEffect(() => {
        axios.get(`${API}/site-meta`).then((r) => setSiteMeta(r.data)).catch(() => setSiteMeta({}));
    }, []);

    const socialPkgs = packages.tiers.filter((t) => t.section !== "campaign");
    const campaignPkgs = packages.tiers.filter((t) => t.section === "campaign");

    return (
        <div className="bg-cafe-cream min-h-screen pb-[76px] md:pb-0">
            <SiteNav />
            <main>
                {/* 1 · Short intro */}
                <Hero c={c.hero} lang={lang} images={images} siteMeta={siteMeta} />
                {/* 2 · Featured real work (hidden if no published projects) */}
                <FeaturedWorkStrip lang={lang} />
                {/* 3 · Streamlined packages (accordion cards) */}
                <Pricing lang={lang} tiers={packages.tiers} addons={packages.addons} zones={packages.zones} status={packages.status} />
                {/* Fees / terms rolled into accordions so they don't repeat in every card */}
                <AdditionalCostsAccordion lang={lang} socialPkgs={socialPkgs} campaignPkgs={campaignPkgs} zones={packages.zones} />
                <BookingTermsAccordion lang={lang} />
                {/* 4 · 3-step process */}
                <SimpleProcess lang={lang} />
                {/* 5 · FAQ */}
                <FAQ c={c.faq} />
                {/* 6 · Contact / final CTA */}
                <FinalCTA c={c.final} lang={lang} />
            </main>
            <SiteFooter />
            <MobileStickyCTA lang={lang} />
        </div>
    );
}


// --- Featured real work strip ---
// Pulls the top 3 PUBLISHED projects with real hero images. Hidden when the
// DB has none (Preview state) — we do NOT pad with stock images. On
// Production this shows real client case studies above the pricing area.
function FeaturedWorkStrip({ lang }) {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/projects`);
                const real = (data || []).filter((p) => p.hero_image && !/\/placeholder-case\.png/.test(p.hero_image));
                const featured = real.filter((p) => p.featured);
                setItems((featured.length ? featured : real).slice(0, 3));
            } catch {
                setItems([]);
            } finally {
                setLoaded(true);
            }
        })();
    }, []);
    if (!loaded || items.length === 0) return null;
    return (
        <section className="bg-cafe-cream pt-4 md:pt-8 pb-12 md:pb-20" data-testid="services-featured-work">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                <div className="flex items-end justify-between gap-6 mb-8 md:mb-10">
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-olive mb-2">
                            {lang === "cn" ? "精选作品" : "Selected work"}
                        </p>
                        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-cafe-espresso leading-tight tracking-[-0.02em]" style={{ fontWeight: 500 }}>
                            {lang === "cn" ? "精选项目。" : "Selected projects."}
                        </h2>
                    </div>
                    <Link to="/work" data-testid="services-featured-view-all"
                        className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.26em] text-cafe-espresso/80 hover:text-cafe-olive border-b border-cafe-espresso/25 hover:border-cafe-olive pb-1 whitespace-nowrap transition-colors mb-2">
                        {lang === "cn" ? "查看全部" : "View all work"}
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {items.map((p) => (
                        <Link key={p.slug} to={`/work/${p.slug}`} data-testid={`services-featured-card-${p.slug}`} className="group block">
                            <div className="relative aspect-[16/10] bg-cafe-olive overflow-hidden">
                                <img src={p.hero_image} alt={p.title} loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                            </div>
                            <p className="mt-3 text-[13px] md:text-sm uppercase tracking-[0.22em] font-medium text-cafe-espresso">{p.title}</p>
                            {p.category && <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-cafe-espresso/55">{p.category}</p>}
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}


// --- Simplified 3-step process ---
// Replaces the old WhatsInside / Perks / TechStack / Guarantee / ROI slabs.
// Three short cards: Say hi → Shoot & edit → Deliver. No "7-day" promise.
function SimpleProcess({ lang }) {
    const copy = lang === "cn" ? {
        kicker: "合作流程", t: "三步沟通,一次交付。",
        steps: [
            ["01", "简报", "聊聊你的品牌、空间、菜品与你希望传达的感觉。"],
            ["02", "拍摄与剪辑", "Kaily 亲自掌镜、调色与剪辑;修改轮次与交付范围按书面报价。"],
            ["03", "交付", "云端相册链接。交付时间与储存期限以书面报价为准。"],
        ],
    } : {
        kicker: "How we work", t: "Three steps. One shoot.",
        steps: [
            ["01", "Brief", "A short chat on your brand, space, dishes and the feeling you want."],
            ["02", "Shoot & edit", "Shot personally by Kaily, hand-graded. Revisions and scope agreed in writing."],
            ["03", "Deliver", "Cloud gallery link. Timeline and storage window are set in the written quotation."],
        ],
    };
    return (
        <Section id="process">
            <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
                <Kicker>{copy.kicker}</Kicker>
                <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso leading-[1.1] tracking-[-0.03em]" style={{ fontWeight: 500 }}>{copy.t}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="services-process">
                {copy.steps.map(([n, t, d]) => (
                    <div key={n} className="rounded-2xl bg-white border border-cafe-espresso/10 p-5 md:p-6">
                        <p className="font-serif text-cafe-olive text-xl md:text-2xl mb-3" style={{ fontWeight: 500 }}>{n}</p>
                        <p className="font-serif text-xl md:text-2xl text-cafe-espresso mb-2" style={{ fontWeight: 500 }}>{t}</p>
                        <p className="text-sm text-cafe-mocha leading-relaxed">{d}</p>
                    </div>
                ))}
            </div>
        </Section>
    );
}


// --- BookingTerms accordion (wraps existing BookingTerms section) ---
// Previously a full-page slab; now collapsed into an accordion so the
// page stays short while terms are still clearly accessible before booking.
function BookingTermsAccordion({ lang }) {
    const [open, setOpen] = useState(false);
    const summary = lang === "cn" ? "预订条款 · Campaign 条件" : "Booking terms · Campaign conditions";
    const helper = lang === "cn" ? "预订确认流程、定金 / 改期政策、超出套餐范围的处理方式,以及 Campaign 套餐的内容授权与长条款。" : "Scope confirmation, deposits, reschedule policy, out-of-scope quotation and Campaign package usage conditions.";
    return (
        <section className="bg-cafe-cream">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 pb-8 md:pb-10">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    data-testid="booking-terms-toggle"
                    className="w-full flex items-center justify-between gap-6 py-5 md:py-6 border-b border-cafe-espresso/15 text-left group hover:border-cafe-olive transition-colors"
                >
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-olive mb-1.5">
                            {lang === "cn" ? "预订条款" : "Booking terms"}
                        </p>
                        <p className="font-serif text-xl md:text-2xl text-cafe-espresso leading-snug" style={{ fontWeight: 500 }}>{summary}</p>
                        <p className="text-sm text-cafe-mocha/80 mt-1.5 max-w-2xl">{helper}</p>
                    </div>
                    {open
                        ? <ChevronUp className="h-5 w-5 text-cafe-espresso flex-shrink-0" strokeWidth={1.5} />
                        : <ChevronDown className="h-5 w-5 text-cafe-espresso flex-shrink-0" strokeWidth={1.5} />}
                </button>
                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            key="terms"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35 }}
                            className="overflow-hidden"
                            data-testid="booking-terms-content"
                        >
                            <div className="pt-6 md:pt-8 space-y-10">
                                <BookingTerms lang={lang} />
                                <CampaignConditionsInline lang={lang} />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}


// --- Campaign conditions block, used inside the Booking terms accordion ---
// Pulled out of the Pricing render so campaign tiers don't trail with a big
// dark slab. We read the same COPY source as before so content stays
// authoritative and never diverges.
function CampaignConditionsInline({ lang }) {
    const c = (PRICING_COPY[lang] || PRICING_COPY.en);
    if (!c.conditions?.length) return null;
    return (
        <div className="bg-cafe-espresso text-cafe-cream rounded-2xl p-6 md:p-8" data-testid="services-campaign-conditions">
            <h3 className="font-serif text-xl md:text-2xl tracking-[-0.02em] mb-4" style={{ fontWeight: 500 }}>{c.conditionsTitle}</h3>
            <ul className="space-y-2">
                {c.conditions.map((line, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-cafe-cream/80 leading-relaxed">
                        <Check className="h-4 w-4 mt-0.5 text-[#D4A574] flex-shrink-0" strokeWidth={2} />{line}
                    </li>
                ))}
            </ul>
            <p className="text-xs text-[#D4A574] mt-5 pt-4 border-t border-cafe-cream/10">{PRICE_FOOTNOTE[lang] || PRICE_FOOTNOTE.en}</p>
        </div>
    );
}


// --- Global "Additional costs" accordion ---
// Collapsible wrapper around the existing TravelPolicy. Kaily wanted ONE
// place that lists travel/parking/tolls/2nd-location fees so individual
// price cards stay lean. We render TravelPolicy inside a <details> element
// that opens on click (and auto-opens if the URL hash lands on it).
function AdditionalCostsAccordion({ lang, socialPkgs, campaignPkgs, zones }) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.location.hash === "#additional-costs" || window.location.hash === "#travel-policy") setOpen(true);
    }, []);
    const summary = lang === "cn"
        ? "附加费用（交通、停车、过路费、第二地点）"
        : "Additional costs (travel, parking, tolls, 2nd location)";
    const helper = lang === "cn"
        ? "点开查看交通费区段、停车/过路费、第二地点费与跨州拍摄的报价方式。"
        : "Tap to see our travel zones, parking/toll pass-through, 2nd-location fee and interstate quotation policy.";
    return (
        <section id="additional-costs" className="bg-cafe-cream">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 py-8 md:py-10">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    data-testid="additional-costs-toggle"
                    className="w-full flex items-center justify-between gap-6 py-5 md:py-6 border-y border-cafe-espresso/15 text-left group hover:border-cafe-olive transition-colors"
                >
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-olive mb-1.5">
                            {lang === "cn" ? "附加费用" : "Additional costs"}
                        </p>
                        <p className="font-serif text-xl md:text-2xl text-cafe-espresso leading-snug" style={{ fontWeight: 500 }}>{summary}</p>
                        <p className="text-sm text-cafe-mocha/80 mt-1.5 max-w-2xl">{helper}</p>
                    </div>
                    {open
                        ? <ChevronUp className="h-5 w-5 text-cafe-espresso flex-shrink-0" strokeWidth={1.5} />
                        : <ChevronDown className="h-5 w-5 text-cafe-espresso flex-shrink-0" strokeWidth={1.5} />}
                </button>
                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            key="costs"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35 }}
                            className="overflow-hidden"
                            data-testid="additional-costs-content"
                        >
                            <div className="pt-6 md:pt-8">
                                <TravelPolicy lang={lang} socialPkgs={socialPkgs} campaignPkgs={campaignPkgs} zones={zones} />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}
